/**
 * Browser hostname → trust surface (source of truth for Watch UI).
 * Rules match optional GET /api/host-surface (Proof API or Cloudflare Pages local handler).
 * Prefer hostname; optional ?surface= only when host is generic (home).
 */

export type HostSurface =
  | "home"
  | "verify"
  | "ledger"
  | "artifact"
  | "notary"
  | "peer"
  | "signals"
  | "digest"
  | "reserve"
  | "pitch"
  | "aether";

const SURFACE_LABELS: Record<HostSurface, string> = {
  home: "XO Watch",
  verify: "Verify Node",
  ledger: "Ledger Node",
  artifact: "Artifact Node",
  notary: "Notary Node",
  peer: "Peer Node",
  signals: "Signals",
  digest: "Digest",
  reserve: "Reserve",
  pitch: "Pitch",
  aether: "Aether",
};

const TRUST_SUB = new Set<string>(["verify", "ledger", "artifact", "notary", "peer"]);

export type HostSurfaceInfo = {
  host: string;
  subdomain: string | null;
  surface: HostSurface;
  label: string;
};

function isHostSurface(s: string): s is HostSurface {
  return s in SURFACE_LABELS;
}

export function normalizeHost(host: string): string {
  let h = (host || "").trim();
  if (!h) return "";
  if (h.includes("]")) {
    const i = h.indexOf("]");
    if (h.startsWith("[") && i > 0) {
      const rest = h.slice(i + 1);
      if (rest.startsWith(":")) h = h.slice(0, i + 1);
    }
  } else if (h.includes(":")) {
    h = h.split(":")[0] || h;
  }
  return h.toLowerCase();
}

export function inferSurfaceFromHost(host: string): HostSurfaceInfo {
  const norm = normalizeHost(host);
  if (!norm) {
    return { host: "", subdomain: null, surface: "home", label: SURFACE_LABELS.home };
  }
  const parts = norm.split(".");
  if (parts.length < 3) {
    return { host: norm, subdomain: null, surface: "home", label: SURFACE_LABELS.home };
  }
  const sub = parts[0] || "";
  if (TRUST_SUB.has(sub) && isHostSurface(sub)) {
    return { host: norm, subdomain: sub, surface: sub, label: SURFACE_LABELS[sub] };
  }
  return { host: norm, subdomain: sub || null, surface: "home", label: SURFACE_LABELS.home };
}

export function getBrowserHostSurface(): HostSurfaceInfo {
  if (typeof window === "undefined" || !window.location) {
    return { host: "", subdomain: null, surface: "home", label: SURFACE_LABELS.home };
  }
  const host = window.location.hostname;
  let info = inferSurfaceFromHost(host);
  if (info.surface === "home") {
    const q = new URLSearchParams(window.location.search).get("surface");
    if (q && isHostSurface(q)) {
      info = {
        ...info,
        surface: q,
        label: SURFACE_LABELS[q],
      };
    }
  }
  return info;
}

/**
 * Dedicated routes exist for /ledger; other trust surfaces use /?surface=… until routes ship.
 */
export function getDefaultRouteForSurface(surface: HostSurface): string {
  switch (surface) {
    case "ledger":
      return "/ledger";
    case "verify":
    case "artifact":
    case "notary":
    case "peer":
      return `/?surface=${encodeURIComponent(surface)}`;
    default:
      return "/";
  }
}
